import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import LabFromRxCTA from "../components/LabFromRxCTA";
import ShareWithDoctorButton from "../components/emergency/ShareWithDoctorButton";
import SendbirdEmbeddedChannel from "../components/sendbird/SendbirdEmbeddedChannel";
import { Send, Stethoscope, Clock, FileText, X, Timer } from "lucide-react";

// v2b — freeze the Sendbird channel 24h after consult ends. Compute display time.
const CONSULT_DURATION_MINUTES = 30;

const useFreezeCountdown = (scheduledAtIso, status) => {
    const [state, setState] = useState({ label: "", freezeAt: null, remaining: "" });
    useEffect(() => {
        if (!scheduledAtIso || status === "cancelled") return undefined;
        const end = new Date(scheduledAtIso).getTime() + CONSULT_DURATION_MINUTES * 60_000;
        const freezeAt = new Date(end + 24 * 60 * 60_000);
        const freezeLabel = freezeAt.toLocaleString(undefined, {
            month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
        });
        const tick = () => {
            const ms = freezeAt.getTime() - Date.now();
            if (ms <= 0) { setState({ label: "frozen", freezeAt, remaining: "" }); return; }
            const h = Math.floor(ms / 3_600_000);
            const m = Math.floor((ms % 3_600_000) / 60_000);
            setState({ label: freezeLabel, freezeAt, remaining: `${h}h ${m}m` });
        };
        tick();
        const id = setInterval(tick, 60_000);
        return () => clearInterval(id);
    }, [scheduledAtIso, status]);
    return state;
};

const ConsultationDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState("");
    const [sending, setSending] = useState(false);
    const listRef = useRef(null);

    const load = () => api.get(`/consultations/${id}`).then((r) => setData(r.data)).finally(() => setLoading(false));
    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { listRef.current && (listRef.current.scrollTop = listRef.current.scrollHeight); }, [data?.consultation?.messages?.length]);

    const send = async () => {
        if (!msg.trim()) return;
        setSending(true);
        try {
            await api.post(`/consultations/${id}/messages`, { content: msg.trim() });
            setMsg("");
            await load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Send failed");
        } finally {
            setSending(false);
        }
    };

    const freeze = useFreezeCountdown(data?.consultation?.scheduled_at, data?.consultation?.status);

    const cancel = async () => {
        if (!window.confirm("Cancel this consultation? Refund policy: full refund if >2h before; 50% if <2h.")) return;
        try {
            const { data: r } = await api.post(`/consultations/${id}/cancel`);
            toast.success(`Cancelled — ₹${r.refund_amount} refunded to wallet (${r.refund_pct}%)`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Cancel failed");
        }
    };

    if (loading || !data) return (<AuthGuard><Navbar /><main className="mx-auto max-w-4xl p-10 text-sm text-zinc-500">Loading…</main></AuthGuard>);
    const c = data.consultation;
    const doc = data.doctor;

    return (
        <AuthGuard>
            <div className="min-h-screen bg-white dark:bg-[#0B0B12]">
                <Navbar />
                <main className="mx-auto max-w-5xl px-6 py-8 lg:px-12">
                    <Link to="/consultations/my" className="text-xs text-zinc-500 hover:underline">← My consultations</Link>
                    <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
                        <div>
                            <p className="label-uppercase text-zinc-500">Consultation</p>
                            <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">{c.doctor_name} <span className="text-sm font-normal text-zinc-500">· {c.doctor_specialization?.replace("-", " ")}</span></h1>
                            <p className="mt-0.5 text-xs text-zinc-500"><Clock className="mr-1 inline h-3 w-3" /> {new Date(c.scheduled_at).toLocaleString()}</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <ShareWithDoctorButton testId="consult-share-doctor" label="Share my profile" className="!py-1.5 !text-xs" />
                            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${c.status === "completed" ? "bg-emerald-100 text-emerald-700" : c.status === "cancelled" ? "bg-rose-100 text-rose-700" : "bg-blue-100 text-blue-700"}`}>{c.status.replace("_", " ")}</span>
                            {["requested", "confirmed"].includes(c.status) && (
                                <button onClick={cancel} className="rounded-md border border-zinc-200 px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50" data-testid="consult-cancel-btn"><X className="mr-1 inline h-3 w-3" />Cancel</button>
                            )}
                        </div>
                    </div>

                    <div className="mt-6 grid gap-4 lg:grid-cols-3">
                        {/* Sendbird embed (Phase 2b) — replaces the historic Mongo chat with a real-time channel */}
                        <div className="lg:col-span-2 space-y-3" data-testid="consult-sendbird-section">
                            <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-brand-200 bg-brand-50/60 px-4 py-2.5">
                                <div className="flex items-center gap-2 text-xs text-brand-900">
                                    <Timer className="h-3.5 w-3.5" />
                                    <span data-testid="sendbird-consult-freeze-countdown">
                                        {freeze.label === "frozen"
                                            ? "This chat is now read-only (24h post-consult freeze)."
                                            : freeze.label
                                                ? `Chat available until ${freeze.label} • ${freeze.remaining} remaining`
                                                : "Chat available for 24h after the consult ends"}
                                    </span>
                                </div>
                            </div>
                            <SendbirdEmbeddedChannel
                                channelUrl={`pills_consult_${c.id}`}
                                customType="doctor_consult"
                                height="520px"
                                testId="sendbird-consult-embed"
                                emptyMessage="Setting up your consultation chat…"
                            />
                        </div>

                        {/* Sidebar */}
                        <aside className="space-y-4">
                            {doc && (
                                <section className="rounded-2xl border border-zinc-100 bg-white p-4 dark:bg-white/[0.03] dark:border-white/10">
                                    <p className="label-uppercase text-zinc-500">Doctor</p>
                                    <p className="mt-2 font-medium text-zinc-900 dark:text-zinc-50">{doc.full_name}</p>
                                    <p className="text-xs text-zinc-500 capitalize">{doc.specialization?.replace("-", " ")} • {doc.qualifications}</p>
                                    <p className="mt-1 text-[11px] text-zinc-500">{doc.hospital_affiliation}</p>
                                </section>
                            )}
                            {c.status === "completed" && (
                                <section className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4 dark:bg-emerald-950/20" data-testid="consult-completed-card">
                                    <p className="flex items-center gap-1 text-sm font-semibold text-emerald-800 dark:text-emerald-200"><Stethoscope className="h-4 w-4" /> Visit summary</p>
                                    {c.doctor_notes && <p className="mt-2 whitespace-pre-line text-xs text-emerald-900 dark:text-emerald-100">{c.doctor_notes}</p>}
                                    {c.follow_up_recommended && <p className="mt-2 text-xs font-medium text-emerald-800 dark:text-emerald-200">Follow-up recommended.</p>}
                                    {c.issued_prescription_id && (
                                        <Link to={`/prescriptions/${c.issued_prescription_id}`} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline" data-testid="consult-rx-link">
                                            <FileText className="h-3 w-3" /> View prescription
                                        </Link>
                                    )}
                                </section>
                            )}
                            {c.status === "completed" && (c.suggested_lab_tests || []).length > 0 && (
                                <LabFromRxCTA tests={c.suggested_lab_tests} context="consultation" />
                            )}
                        </aside>
                    </div>
                </main>
                <Footer />
            </div>
        </AuthGuard>
    );
};

export default ConsultationDetail;
